import {
  Sprout,
  Layers,
  LineChart,
  Sparkles,
  Network,
  ArrowRight,
  Quote,
} from "lucide-react";

const stages = [
  {
    n: "01",
    icon: Sprout,
    label: "Foundational",
    tagline: "Build the base.",
    body:
      "Strong thesis, a validated product, and a first target account list — but the operating layer was still spreadsheets and hand-enrichment.",
    metrics: ["100% FY23 retention", "9:1 LTV : CAC (18:1 enterprise)", "80+ CSAT"],
  },
  {
    n: "02",
    icon: Layers,
    label: "Structured",
    tagline: "Define the target.",
    body:
      "Off a bespoke, unadopted legacy CRM and onto HubSpot — right-sized for how the team actually works. Stages redefined around buyer actions, not seller optimism.",
    metrics: ["Legacy CRM → HubSpot", "Pipeline stages redesigned", "First adopted system of record"],
  },
  {
    n: "03",
    icon: LineChart,
    label: "Data-Driven",
    tagline: "Measure what matters.",
    body:
      "January '25 HubSpot go-live turned the account-based hypothesis into hard evidence. Target vs non-target — proven, not preferred.",
    metrics: ["~2× conversion rate", "+48% larger deal size", "Jan '25 go-live"],
  },
  {
    n: "04",
    icon: Sparkles,
    label: "AI-Augmented",
    tagline: "Amplify with AI.",
    body:
      "AI moved from content help into operational infrastructure. Production agents went live across the GTM function — sales, commercial, marketing.",
    metrics: [
      "SPICED call analysis",
      "Proposal & SOW generator",
      "Content generation suite",
      "80%+ content production automated",
    ],
  },
  {
    n: "05",
    icon: Network,
    label: "Agentic GTM",
    tagline: "Autonomous orchestration.",
    body:
      "An agentic layer stitched every function, data source and channel into one engine: signal → auto-enrich → outreach → human review → activate.",
    metrics: [
      "15+ integrated platforms",
      "~850 tiered target accounts",
      "10+ codified skills",
      "4 live signal tables",
    ],
  },
];

const before = {
  label: "Month 0",
  title: "Ambition outpaced operations.",
  points: [
    "One spreadsheet, ~1,000 accounts",
    "CRM sat unused for over a year",
    "Every account enriched by hand",
    "Manual, fragile, unrepeatable",
  ],
};

const after = {
  label: "Month 28",
  title: "An AI-orchestrated engine.",
  points: [
    "15+ integrated platforms",
    "Signal → enrich → outreach, orchestrated",
    "~850 target accounts, tiered",
    "Strategy → pipeline: months to days",
  ],
};

const dimensions = ["People", "Process", "Data", "Technology"];

export const CaseStudy = () => {
  return (
    <section
      id="case-study"
      data-testid="case-study-section"
      className="relative py-24 md:py-32 border-t border-white/5"
    >
      <div
        className="absolute inset-0 -z-10 opacity-60"
        style={{
          background:
            "radial-gradient(ellipse 55% 45% at 20% 10%, rgba(124,58,237,0.16), transparent 70%)",
        }}
        aria-hidden
      />
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Header */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-6 items-end mb-14">
          <div className="md:col-span-8">
            <div className="text-xs font-mono-ui uppercase tracking-[0.28em] text-violet-400 mb-4">
              ─── Case Study · The GTM Maturity Journey
            </div>
            <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-light tracking-tighter text-zinc-50 leading-[1.02]">
              From a single spreadsheet to
              <br />
              <span className="text-zinc-500">an AI-orchestrated GTM engine.</span>
            </h2>
          </div>
          <div className="md:col-span-4">
            <p className="text-sm md:text-base text-zinc-400 leading-relaxed">
              Chris led this 28-month build in a{" "}
              <span className="text-zinc-200">fractional CGO</span> capacity —
              designing the operating system alongside the in-house growth team
              and implementation partners. Brand available on request.
            </p>
          </div>
        </div>

        {/* Intro narrative */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-10 mb-14">
          <div className="md:col-span-7">
            <p className="font-display text-xl md:text-2xl text-zinc-200 leading-snug tracking-tight max-w-2xl">
              Pipeline lived in spreadsheets. The CRM sat unused for over a
              year. Every account enriched by hand.{" "}
              <span className="text-zinc-500">
                From that starting line — one thesis, one journey, five stages
                — a maturity model from manual operations to autonomous
                orchestration.
              </span>
            </p>
          </div>
          <div className="md:col-span-5">
            <div className="border border-white/10 bg-zinc-950/60 p-5">
              <div className="text-[11px] font-mono-ui uppercase tracking-[0.22em] text-zinc-500 mb-4">
                Measured across four dimensions
              </div>
              <div className="grid grid-cols-2 gap-2">
                {dimensions.map((d) => (
                  <div
                    key={d}
                    className="flex items-center gap-2 border border-white/5 bg-white/[0.02] px-3 py-2.5"
                    data-testid={`case-dimension-${d.toLowerCase()}`}
                  >
                    <span className="h-1.5 w-1.5 bg-violet-400" />
                    <span className="text-sm text-zinc-200">{d}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Before → After split */}
        <div
          data-testid="case-study-before-after"
          className="relative border border-white/10 bg-zinc-950/60 grid grid-cols-1 md:grid-cols-[1fr_auto_1fr] items-stretch mb-16"
        >
          <div className="p-8 md:p-10">
            <div className="text-[11px] font-mono-ui uppercase tracking-[0.24em] text-zinc-500 mb-3">
              ▲ Before · {before.label}
            </div>
            <div className="font-display text-2xl text-zinc-50 leading-tight mb-6">
              {before.title}
            </div>
            <ul className="space-y-2.5">
              {before.points.map((p) => (
                <li key={p} className="flex items-start gap-3 text-sm text-zinc-400">
                  <span className="mt-2 h-px w-4 bg-zinc-700 shrink-0" />
                  <span className="leading-relaxed">{p}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Divider / arrow */}
          <div className="hidden md:flex flex-col items-center justify-center px-6 border-x border-white/5 bg-violet-500/[0.03]">
            <div className="font-mono-ui text-[10px] uppercase tracking-[0.28em] text-violet-300 rotate-180 [writing-mode:vertical-rl] py-4">
              28 Months
            </div>
            <ArrowRight className="h-5 w-5 text-violet-400" />
          </div>
          <div className="md:hidden flex items-center justify-center py-3 border-y border-white/5 bg-violet-500/[0.03]">
            <span className="font-mono-ui text-[10px] uppercase tracking-[0.28em] text-violet-300 mr-2">
              28 months
            </span>
            <ArrowRight className="h-4 w-4 text-violet-400" />
          </div>

          <div className="p-8 md:p-10 bg-violet-500/[0.04]">
            <div className="text-[11px] font-mono-ui uppercase tracking-[0.24em] text-violet-300 mb-3">
              ▲ After · {after.label}
            </div>
            <div className="font-display text-2xl text-zinc-50 leading-tight mb-6">
              {after.title}
            </div>
            <ul className="space-y-2.5">
              {after.points.map((p) => (
                <li key={p} className="flex items-start gap-3 text-sm text-zinc-200">
                  <span className="mt-2 h-px w-4 bg-violet-400 shrink-0" />
                  <span className="leading-relaxed">{p}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* The 5 stages */}
        <div className="mb-16">
          <div className="flex items-baseline justify-between mb-6">
            <div className="text-[11px] font-mono-ui uppercase tracking-[0.24em] text-zinc-500">
              ─── One journey, five stages
            </div>
            <div className="hidden md:block text-[11px] font-mono-ui uppercase tracking-[0.24em] text-zinc-600">
              Manual ops ─── ─── ─── ─── Autonomous orchestration
            </div>
          </div>
          <ol className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-px bg-white/5 border border-white/5">
            {stages.map((s) => {
              const Icon = s.icon;
              return (
                <li
                  key={s.n}
                  data-testid={`case-study-stage-${s.n}`}
                  className="bg-zinc-950 p-6 flex flex-col card-hover"
                >
                  <div className="flex items-center justify-between mb-6">
                    <span className="font-mono-ui text-[10px] uppercase tracking-[0.22em] text-violet-300">
                      Stage {s.n}
                    </span>
                    <span className="inline-flex h-8 w-8 items-center justify-center border border-white/10">
                      <Icon className="h-3.5 w-3.5 text-zinc-300" strokeWidth={1.6} />
                    </span>
                  </div>
                  <div className="text-[11px] font-mono-ui uppercase tracking-[0.2em] text-zinc-500 mb-2">
                    {s.label}
                  </div>
                  <div className="font-display text-base text-zinc-50 leading-snug mb-2">
                    {s.tagline}
                  </div>
                  <p className="text-xs text-zinc-400 leading-relaxed">
                    {s.body}
                  </p>
                  <ul className="mt-5 space-y-1.5 border-t border-white/5 pt-4">
                    {s.metrics.map((m) => (
                      <li
                        key={m}
                        className="flex items-start gap-2 text-[11px] text-zinc-300 leading-snug"
                      >
                        <span className="mt-1.5 h-px w-2.5 bg-violet-400/70 shrink-0" />
                        {m}
                      </li>
                    ))}
                  </ul>
                </li>
              );
            })}
          </ol>
        </div>

        {/* Closing thesis card */}
        <div
          data-testid="case-study-thesis"
          className="border border-violet-500/30 bg-violet-500/[0.04] p-8 md:p-10 relative overflow-hidden"
        >
          <Quote
            className="absolute -top-2 -left-2 h-16 w-16 text-violet-500/10"
            strokeWidth={1}
            aria-hidden
          />
          <div className="relative grid grid-cols-1 md:grid-cols-12 gap-6 items-center">
            <div className="md:col-span-8">
              <p className="font-display text-2xl md:text-3xl text-zinc-50 leading-snug tracking-tight">
                Not AI replacing human work.{" "}
                <span className="text-violet-300">
                  AI removing the bottleneck between strategic intent and
                  operational execution.
                </span>
              </p>
              <p className="mt-4 text-sm font-mono-ui uppercase tracking-[0.22em] text-zinc-400">
                Lean by design · Intelligent by architecture
              </p>
            </div>
            <div className="md:col-span-4 md:border-l md:border-white/10 md:pl-8">
              <div className="text-[11px] font-mono-ui uppercase tracking-[0.22em] text-zinc-500 mb-2">
                The outcome that matters
              </div>
              <div className="font-display text-xl text-zinc-50 leading-tight">
                Strategy to pipeline,
                <br />
                once measured in months
              </div>
              <div className="mt-2 inline-flex items-center gap-2 text-violet-300 font-display text-xl">
                <ArrowRight className="h-4 w-4" />
                <span>now measured in days.</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
